import ExpandMore from '@mui/icons-material/ExpandMore';
import Moving from '@mui/icons-material/Moving';
import Restore from '@mui/icons-material/Restore';
import UnfoldLess from '@mui/icons-material/UnfoldLess';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import {
  GeofenceCalculationStrategy,
  GeofenceStrategy,
  GeofenceTrigger_CalculateForJob,
  GeofenceType,
  postV1CompaniesCompanyIdJobsJobIdGeofenceTriggers,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import Label from '~components/FormFields/v2/Label';
import { CommonLoader } from '~components/Loaders/CommonLoader';
import Tabs from '~components/Tabs/Tabs';
import { GeofenceTrigger } from '~hooks/useGeofenceTriggers/models';
import { Job } from '~hooks/useJob/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { getLocationHistoriesByMovingSite } from '~hooks/useLocationHistory/useLocationHistory';
import { isDefaultSiteUsages, type JobSiteUsage } from '~hooks/useSiteUsages/models';
import {
  buildTimedPath,
  interpolatePositionAtTime,
} from '~pages/Admin/Geo/Distances/DistanceMap/catmullRomSpline';
import { createWaypointSvgDataUrl } from '~pages/Admin/Geo/mapIcons';
import { ROUTES } from '~router/routePaths';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { CalculatedGeofenceTrigger } from './CalculatedGeofenceTrigger';
import {
  buildSideBySideDiffRows,
  buildWaypointMarkers,
  getMovingSiteInfo,
  getTotalLoadCount,
} from './derivations';
import {
  buildGeofencesForApi,
  buildSiteOverridesFromSiteUsages,
  GeofenceOverride,
  SiteOverride,
} from './geofence-utils';
import { GeofenceDebugMap } from './GeofenceDebugMap';
import { headerCellSx } from './geofenceTableStyles';
import { GeofenceTimeline } from './GeofenceTimeline';
import JobEventsTable from './JobEventsTable';
import { LocationHistoriesTable } from './LocationHistoriesTable';
import { SiteOverridePanel } from './SiteOverridePanel';
import {
  BottomTab,
  isBottomTab,
  LOCATION_HISTORIES_PAGE_SIZE,
  parsePage,
} from './url-params';

const TRIGGER_TYPE_ENTER_KEY = 'admin.job_geo_debug.geofence_entry';
const TRIGGER_TYPE_EXIT_KEY = 'admin.job_geo_debug.geofence_exit';

function toApiStrategy(strategy: GeofenceStrategy): GeofenceCalculationStrategy {
  switch (strategy) {
    case GeofenceStrategy.NAIVE:
      return GeofenceCalculationStrategy.NAIVE;
    case GeofenceStrategy.ACCURACY_STRICT:
      return GeofenceCalculationStrategy.ACCURACY_STRICT;
    case GeofenceStrategy.LEGACY:
      return GeofenceCalculationStrategy.HISTORICAL;
  }
}
/** Delay (ms) before fetching triggers after strategy/delay change to avoid a request per keypress. */
const TRIGGERS_FETCH_DEBOUNCE_MS = 400;

/** Cache for historical geofence triggers by job; key = `${companyId}:${jobId}`. Data is immutable. */
const historicalTriggersCache = new Map<string, GeofenceTrigger[]>();

const diffGroupHeaderCellSx: SxProps<Theme> = {
  fontWeight: 600,
  backgroundColor: (theme) => theme.semantic.surfaceSunken,
  borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
};

interface GeoDebugPaneProps {
  job: Job | null;
  jobId: string;
  companyId: string | null;
  isFeet: boolean;
  siteUsages: JobSiteUsage[];
  pings: LocationHistoryMinimal[];
}

const GeoDebugPane = ({
  job,
  jobId,
  companyId,
  isFeet,
  siteUsages,
  pings: contextPings,
}: GeoDebugPaneProps) => {
  const [strategy, setStrategy] = useState<GeofenceStrategy>(
    job?.geofenceStrategy ?? GeofenceStrategy.LEGACY,
  );
  const [siteOverrides, setSiteOverrides] = useState<SiteOverride[]>([]);
  const [delaySecondsInput, setDelaySecondsInput] = useState('0');
  const delaySecondsInputRef = useRef(delaySecondsInput);
  delaySecondsInputRef.current = delaySecondsInput;
  const siteOverridesRef = useRef(siteOverrides);
  siteOverridesRef.current = siteOverrides;

  /** Cached site overrides from waypoints endpoint, used for reset. */
  const waypointOverridesCacheRef = useRef<SiteOverride[]>([]);

  const [locationTab, setLocationTab] = useState<number | null>(null);

  const [hoveredTriggerLocation, setHoveredTriggerLocation] = useState<{
    lat: string;
    lon: string;
  } | null>(null);

  const [diffViewExpanded, setDiffViewExpanded] = useState(true);

  const [triggers, setTriggers] = useState<GeofenceTrigger[]>([]);
  const [historicalTriggers, setHistoricalTriggers] = useState<GeofenceTrigger[] | null>(
    null,
  );
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [triggersError, setTriggersError] = useState(false);
  const pings = contextPings;
  const [indicatorTime, setIndicatorTime] = useState<number | null>(null);
  const [movingSitePings, setMovingSitePings] = useState<LocationHistoryMinimal[]>([]);

  const { bottomTab: bottomTabParam, page: pageParam } = useParams<{
    bottomTab: string;
    page: string;
  }>();
  const navigate = useNavigate();
  const bottomTab: BottomTab = isBottomTab(bottomTabParam)
    ? bottomTabParam
    : 'geofence_triggers';
  const parsedPage = parsePage(pageParam);
  const locationHistoriesPage = parsedPage ?? 1;
  const geofencesBase = `/${ROUTES.admin.base}/${ROUTES.admin.geo}/jobs/${jobId}/geofences`;
  const setBottomTab = useCallback(
    (value: BottomTab) => {
      const suffix = value === 'location_histories' ? '/1' : '';
      navigate(`${geofencesBase}/${value}${suffix}`, { replace: true });
    },
    [navigate, geofencesBase],
  );
  const setLocationHistoriesPage = useCallback(
    (page: number) => {
      navigate(`${geofencesBase}/location_histories/${page}`, { replace: true });
    },
    [navigate, geofencesBase],
  );
  useEffect(() => {
    if (bottomTabParam !== undefined && !isBottomTab(bottomTabParam)) {
      navigate(`${geofencesBase}/geofence_triggers`, { replace: true });
      return;
    }
    if (bottomTab === 'location_histories' && parsedPage === null) {
      navigate(`${geofencesBase}/location_histories/1`, { replace: true });
      return;
    }
    if (bottomTab !== 'location_histories' && pageParam !== undefined) {
      navigate(`${geofencesBase}/${bottomTab}`, { replace: true });
    }
  }, [bottomTab, bottomTabParam, pageParam, parsedPage, geofencesBase, navigate]);

  // Clamp the location_histories page to the last valid page after pings load.
  const locationHistoriesTotalPages = Math.max(
    1,
    Math.ceil(pings.length / LOCATION_HISTORIES_PAGE_SIZE),
  );
  useEffect(() => {
    if (
      bottomTab === 'location_histories' &&
      parsedPage !== null &&
      parsedPage > locationHistoriesTotalPages
    ) {
      navigate(`${geofencesBase}/location_histories/${locationHistoriesTotalPages}`, {
        replace: true,
      });
    }
  }, [bottomTab, parsedPage, locationHistoriesTotalPages, geofencesBase, navigate]);
  const [rawTriggerResponse, setRawTriggerResponse] = useState<
    CalculatedGeofenceTrigger[] | null
  >(null);

  const parsedJobSites = useMemo(() => siteUsages.map((su) => su.site), [siteUsages]);

  const totalLoadCount = useMemo(() => getTotalLoadCount(siteUsages), [siteUsages]);

  const movingSiteInfo = useMemo(() => getMovingSiteInfo(siteUsages), [siteUsages]);

  const timedPath = useMemo(() => buildTimedPath(pings), [pings]);
  const movingSiteTimedPath = useMemo(
    () => buildTimedPath(movingSitePings),
    [movingSitePings],
  );

  const truckMarker = useMemo(() => {
    if (indicatorTime === null) return null;
    return interpolatePositionAtTime(timedPath, indicatorTime);
  }, [indicatorTime, timedPath]);

  const movingSitePosition = useMemo(() => {
    if (indicatorTime === null || !movingSiteInfo) return null;
    const { rawPath, pingTimes } = movingSiteTimedPath;
    if (rawPath.length === 0) return null;

    // clamp to nearest endpoint so the geofence stays visible even when the
    // scrubber is outside the moving site's time range
    const clampedTime = Math.max(
      pingTimes[0],
      Math.min(indicatorTime, pingTimes[pingTimes.length - 1]),
    );
    const result = interpolatePositionAtTime(movingSiteTimedPath, clampedTime);
    if (!result) return null;
    return { position: result.position, radius: movingSiteInfo.radius };
  }, [indicatorTime, movingSiteTimedPath, movingSiteInfo]);

  const isLegacy = strategy === GeofenceStrategy.LEGACY;
  const isEditable = !isLegacy;
  /** Tracks whether the user has changed any input from the job's persisted values. */
  const [isDirty, setIsDirty] = useState(false);

  // Reset user-editable fields when the job changes.
  useEffect(() => {
    setLocationTab(null);
    setDelaySecondsInput(String(job?.geofenceDelaySeconds ?? 0));
    setStrategy(job?.geofenceStrategy ?? GeofenceStrategy.LEGACY);
    setRawTriggerResponse(null);
    setIsDirty(false);
  }, [job]);

  useEffect(() => {
    setRawTriggerResponse(null);
  }, [strategy]);

  // Build site overrides from site_usages (the authoritative source).
  useEffect(() => {
    if (siteUsages.length === 0) {
      setSiteOverrides([]);
      waypointOverridesCacheRef.current = [];
      return;
    }
    const overrides = buildSiteOverridesFromSiteUsages(siteUsages);
    waypointOverridesCacheRef.current = overrides;
    setSiteOverrides(overrides);
  }, [siteUsages]);

  // Fetch moving site location history when available.
  useEffect(() => {
    if (!jobId || !companyId || !movingSiteInfo) {
      setMovingSitePings([]);
      return;
    }
    const controller = new AbortController();
    getLocationHistoriesByMovingSite(
      companyId,
      jobId,
      movingSiteInfo.movingSiteId,
      controller.signal,
    )
      .then((result) => {
        if (!controller.signal.aborted) setMovingSitePings(result);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [jobId, companyId, movingSiteInfo]);

  const loadTriggers = useCallback(async () => {
    if (!jobId || !companyId) return;
    setTriggersError(false);
    setTriggersLoading(true);
    const cacheKey = `${companyId}:${jobId}`;
    const apiStrategy = toApiStrategy(strategy);

    if (apiStrategy === GeofenceCalculationStrategy.HISTORICAL) {
      const cached = historicalTriggersCache.get(cacheKey);
      if (cached !== undefined) {
        setTriggers(cached);
        setTriggersLoading(false);
        return;
      }
    }

    try {
      const input = delaySecondsInputRef.current;
      const parsedDelay = parseInt(input, 10);
      const delaySeconds = Number.isNaN(parsedDelay) || parsedDelay < 0 ? 0 : parsedDelay;
      const body: GeofenceTrigger_CalculateForJob = {
        strategy: apiStrategy,
        ...(apiStrategy !== GeofenceCalculationStrategy.HISTORICAL && {
          delay_seconds: delaySeconds,
          geofences: buildGeofencesForApi(siteOverridesRef.current),
        }),
      };
      const response = await postV1CompaniesCompanyIdJobsJobIdGeofenceTriggers({
        path: { 'company-id': companyId, 'job-id': jobId },
        body,
      });
      const rawData = response.data.data ?? [];
      setRawTriggerResponse(
        rawData.map((proto) => CalculatedGeofenceTrigger.parse(proto)),
      );
      const parsed = rawData.map((proto) => GeofenceTrigger.parse(proto));
      if (apiStrategy === GeofenceCalculationStrategy.HISTORICAL) {
        historicalTriggersCache.set(cacheKey, parsed);
      }
      setTriggers(parsed);
    } catch (error) {
      setRawTriggerResponse(null);
      setTriggersError(true);
      try {
        connection.handleRequestError(
          error,
          t('error_messages.geofence_triggers.failed_to_fetch'),
        );
      } catch {
        // error already handled (toast + Sentry)
      }
    } finally {
      setTriggersLoading(false);
    }
  }, [jobId, companyId, strategy]);

  const loadHistoricalTriggers = useCallback(async () => {
    if (!jobId || !companyId) return;
    const cacheKey = `${companyId}:${jobId}`;
    const cached = historicalTriggersCache.get(cacheKey);
    if (cached !== undefined) {
      setHistoricalTriggers(cached);
      return;
    }
    try {
      const response = await postV1CompaniesCompanyIdJobsJobIdGeofenceTriggers({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: { strategy: GeofenceCalculationStrategy.HISTORICAL },
      });
      const parsed = (response.data.data ?? []).map((proto) =>
        GeofenceTrigger.parse(proto),
      );
      historicalTriggersCache.set(cacheKey, parsed);
      setHistoricalTriggers(parsed);
    } catch {
      setHistoricalTriggers([]);
    }
  }, [jobId, companyId]);

  useEffect(() => {
    if (!jobId || !companyId) return;
    const timer = setTimeout(() => {
      loadTriggers();
    }, TRIGGERS_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [jobId, companyId, strategy, siteUsages, loadTriggers]);

  useEffect(() => {
    if (!isDirty) {
      setHistoricalTriggers(null);
      return;
    }
    loadHistoricalTriggers();
  }, [isDirty, loadHistoricalTriggers]);

  const handleDelayBlur = useCallback(() => {
    loadTriggers();
  }, [loadTriggers]);

  /** All geofences for the debug map, with the selected one marked. */
  const debugMapGeofences = useMemo(() => {
    const result: {
      key: string;
      type: GeofenceType;
      tag: string | null;
      polygonCoordinates: { lat: number; lng: number }[];
      circleCenter: { lat: number; lng: number } | null;
      circleRadius: number | null;
      isSelected: boolean;
      isMoving: boolean;
      isDefaultSite: boolean;
    }[] = [];
    siteOverrides.forEach((site, siteIndex) => {
      const isMovingSite =
        movingSiteInfo !== null && siteIndex === movingSiteInfo.siteIndex;
      const isDefaultSite = isDefaultSiteUsages(site.usages ?? []);
      site.geofences.forEach((gf, i) => {
        const movingCenter =
          isMovingSite && movingSitePosition
            ? {
                lat: movingSitePosition.position.lat,
                lng: movingSitePosition.position.lng,
              }
            : null;
        result.push({
          key: `${siteIndex}-${gf.key}`,
          type: gf.geofenceType,
          tag: gf.tag,
          polygonCoordinates: gf.polygonCoordinates,
          circleCenter: movingCenter ?? gf.circleCenter,
          circleRadius: gf.circleRadius,
          isSelected: siteIndex === locationTab && i === site.selectedGeofenceIndex,
          isMoving: isMovingSite,
          isDefaultSite,
        });
      });
    });
    return result;
  }, [siteOverrides, locationTab, movingSiteInfo, movingSitePosition]);

  const waypointMarkers = useMemo(
    () => buildWaypointMarkers(siteUsages, movingSiteInfo, movingSitePosition),
    [siteUsages, movingSiteInfo, movingSitePosition],
  );

  const mapEditTriggerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedLoadTriggers = useCallback(() => {
    if (mapEditTriggerTimerRef.current) clearTimeout(mapEditTriggerTimerRef.current);
    mapEditTriggerTimerRef.current = setTimeout(() => {
      mapEditTriggerTimerRef.current = null;
      loadTriggers();
    }, 100);
  }, [loadTriggers]);

  const updateSelectedGeofence = useCallback(
    (patch: Partial<GeofenceOverride>) => {
      if (locationTab === null) return;
      setSiteOverrides((prev) => {
        const site = prev[locationTab];
        if (!site) return prev;
        const idx = site.selectedGeofenceIndex;
        const geofences = site.geofences.map((gf, i) =>
          i === idx ? { ...gf, ...patch } : gf,
        );
        const next = prev.map((s, i) => (i === locationTab ? { ...s, geofences } : s));
        siteOverridesRef.current = next;
        return next;
      });
      setIsDirty(true);
    },
    [locationTab],
  );

  const handleGeofencePolygonEdit = useCallback(
    (coordinates: { lat: number; lng: number }[]) => {
      updateSelectedGeofence({ polygonCoordinates: coordinates });
      debouncedLoadTriggers();
    },
    [updateSelectedGeofence, debouncedLoadTriggers],
  );

  const handleGeofenceCircleEdit = useCallback(
    (center: { lat: number; lng: number }, radius: number) => {
      if (locationTab === null) return;
      const site = siteOverridesRef.current[locationTab];
      if (!site) return;
      const gf = site.geofences[site.selectedGeofenceIndex];
      if (!gf) return;
      const centerUnchanged =
        gf.circleCenter &&
        Math.abs(gf.circleCenter.lat - center.lat) < 1e-8 &&
        Math.abs(gf.circleCenter.lng - center.lng) < 1e-8;
      const radiusUnchanged =
        gf.circleRadius !== null && Math.abs(gf.circleRadius - radius) < 0.1;
      if (centerUnchanged && radiusUnchanged) return;
      updateSelectedGeofence({
        circleCenter: center,
        circleRadius: Math.round(radius),
      });
      debouncedLoadTriggers();
    },
    [locationTab, updateSelectedGeofence, debouncedLoadTriggers],
  );

  const handleGeofenceMapClick = useCallback((compositeKey: string) => {
    // Keys are formatted as `${siteIndex}-${gf.key}` in debugMapGeofences
    for (let siteIndex = 0; siteIndex < siteOverridesRef.current.length; siteIndex++) {
      const site = siteOverridesRef.current[siteIndex];
      const idx = site.geofences.findIndex(
        (gf) => `${siteIndex}-${gf.key}` === compositeKey,
      );
      if (idx !== -1) {
        setLocationTab(siteIndex);
        setSiteOverrides((prev) => {
          const next = prev.map((s, i) =>
            i === siteIndex ? { ...s, selectedGeofenceIndex: idx } : s,
          );
          siteOverridesRef.current = next;
          return next;
        });
        return;
      }
    }
  }, []);

  const handleReset = useCallback(() => {
    if (!job) return;
    const resetStrategy = job.geofenceStrategy ?? GeofenceStrategy.LEGACY;
    const resetDelay = String(job.geofenceDelaySeconds ?? 0);
    const next = waypointOverridesCacheRef.current.length
      ? waypointOverridesCacheRef.current
      : buildSiteOverridesFromSiteUsages(siteUsages);
    setStrategy(resetStrategy);
    setDelaySecondsInput(resetDelay);
    delaySecondsInputRef.current = resetDelay;
    siteOverridesRef.current = next;
    setSiteOverrides(next);
    setIsDirty(false);
    // The trigger-loading effect re-fires on strategy change; for delay-only
    // or geofence-only resets, fire loadTriggers directly so the displayed
    // triggers reflect the restored values.
    if (strategy === resetStrategy) {
      loadTriggers();
    }
  }, [job, siteUsages, strategy, loadTriggers]);

  const setWaypointOverride = useCallback(
    (
      waypointIndex: number,
      update: Partial<SiteOverride> | ((prev: SiteOverride) => SiteOverride),
    ) => {
      setSiteOverrides((prev) => {
        const next = prev.map((s, i) => {
          if (i !== waypointIndex) return s;
          return typeof update === 'function' ? update(s) : { ...s, ...update };
        });
        siteOverridesRef.current = next;
        return next;
      });
      setIsDirty(true);
    },
    [],
  );

  /** View-only: highlight a different geofence on the map without marking the form dirty. */
  const selectWaypointGeofence = useCallback((waypointIndex: number, gfIndex: number) => {
    setSiteOverrides((prev) => {
      const site = prev[waypointIndex];
      if (!site || site.selectedGeofenceIndex === gfIndex) return prev;
      const next = prev.map((s, i) =>
        i === waypointIndex ? { ...s, selectedGeofenceIndex: gfIndex } : s,
      );
      siteOverridesRef.current = next;
      return next;
    });
  }, []);

  const formatTriggerType = (triggerType: string) => {
    if (triggerType === 'enter') return t(TRIGGER_TYPE_ENTER_KEY);
    if (triggerType === 'exit') return t(TRIGGER_TYPE_EXIT_KEY);
    return triggerType;
  };

  const formatGeofenceTag = (tag: string | null) => {
    if (!tag) return '—';
    const key = `admin.job_geo_debug.geofence_tag_${tag}`;
    const translated = t(key);
    return translated === key ? tag : translated;
  };

  const showSideBySideDiff = isDirty && historicalTriggers !== null;

  /** Merged diff rows: each row has optional historical and optional current trigger, sorted by time. Aligns left and right tables. */
  const sideBySideDiffRows = useMemo(
    () =>
      showSideBySideDiff ? buildSideBySideDiffRows(historicalTriggers, triggers) : [],
    [triggers, historicalTriggers, showSideBySideDiff],
  );

  /** Single list for when we're not showing diff (historical strategy or loading). */
  const triggersForSingleView = useMemo(
    () => (showSideBySideDiff ? [] : triggers),
    [showSideBySideDiff, triggers],
  );

  const pagedPings = useMemo(() => {
    const start = (locationHistoriesPage - 1) * LOCATION_HISTORIES_PAGE_SIZE;
    return pings.slice(start, start + LOCATION_HISTORIES_PAGE_SIZE);
  }, [pings, locationHistoriesPage]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        position: 'relative',
      }}
    >
      {triggersLoading && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(10, 24, 32, 0.7)'
                : 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CircularProgress size={32} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('actions.loading')}
            </Typography>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 2,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            flex: '0 0 auto',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflow: 'auto',
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Label sx={{ flex: 1, mb: 0.5 }}>
                  {t('admin.job_geo_debug.strategy_label')}
                </Label>
                {isDirty && (
                  <Tooltip title={t('admin.job_geo_debug.reset_to_job_defaults')}>
                    <IconButton
                      size="small"
                      onClick={handleReset}
                      aria-label={t('admin.job_geo_debug.reset_to_job_defaults')}
                    >
                      <Restore fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Tabs
                selected={strategy}
                onChange={(value) => {
                  setStrategy(value);
                  setIsDirty(true);
                }}
                tabs={[
                  {
                    label: t('admin.job_geo_debug.strategy_legacy'),
                    value: GeofenceStrategy.LEGACY,
                    disabled: strategy !== GeofenceStrategy.LEGACY,
                  },
                  {
                    label: t('admin.job_geo_debug.strategy_naive'),
                    value: GeofenceStrategy.NAIVE,
                  },
                  {
                    label: t('admin.job_geo_debug.strategy_accuracy_strict'),
                    value: GeofenceStrategy.ACCURACY_STRICT,
                  },
                ]}
              />
            </Box>

            {job && (
              <Box>
                <Label sx={{ mb: 0.5 }}>
                  {t('admin.job_geo_debug.delay_seconds_label')}
                </Label>
                <TextField
                  type="number"
                  value={
                    isEditable
                      ? delaySecondsInput
                      : String(job?.geofenceDelaySeconds ?? 0)
                  }
                  onChange={(e) => {
                    setDelaySecondsInput(e.target.value);
                    setIsDirty(true);
                  }}
                  onBlur={isEditable ? handleDelayBlur : undefined}
                  slotProps={{ htmlInput: { min: 0, step: 1 } }}
                  size="small"
                  fullWidth
                  disabled={!isEditable}
                  sx={{
                    '& .MuiInputBase-root': { height: 30 },
                    '& .MuiInputBase-input': { fontSize: '12px', py: 0 },
                  }}
                />
              </Box>
            )}

            {job && siteOverrides.length > 0 && (
              <Box
                sx={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflow: 'auto',
                }}
              >
                <Label sx={{ mb: 0.5, pl: '9px' }}>
                  {t('admin.job_geo_debug.sites_label')}
                </Label>
                {siteOverrides.map((so, i) => (
                  <Accordion
                    key={siteUsages[i]?.site.id ?? i}
                    expanded={locationTab === i}
                    onChange={(_, expanded) => {
                      setLocationTab(expanded ? i : null);
                    }}
                    disableGutters
                    sx={{
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      '&:not(:first-of-type)': { borderTop: 0 },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        minHeight: 36,
                        px: 1,
                        '& .MuiAccordionSummary-content': {
                          my: 0.5,
                          minWidth: 0,
                          overflow: 'hidden',
                          alignItems: 'center',
                          gap: 0.75,
                        },
                      }}
                    >
                      <img
                        src={createWaypointSvgDataUrl(
                          siteUsages[i]?.usages.length > 1
                            ? 'both'
                            : (siteUsages[i]?.usages[0]?.waypointType ??
                                WaypointType.PICKUP),
                          isDefaultSiteUsages(siteUsages[i]?.usages ?? []),
                        )}
                        alt=""
                        width={18}
                        height={18}
                        style={{ flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontWeight: locationTab === i ? 600 : 400,
                          flex: 1,
                        }}
                      >
                        {so.name}
                      </Typography>
                      {so.geofences.some((gf) => gf.movingGeofence) && (
                        <Moving
                          sx={{
                            fontSize: '1rem',
                            color: 'text.secondary',
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
                      <SiteOverridePanel
                        override={so}
                        setOverride={(update) => setWaypointOverride(i, update)}
                        onSelectGeofence={(gfIndex) => selectWaypointGeofence(i, gfIndex)}
                        jobSite={parsedJobSites[i] ?? null}
                        isEditable={isEditable}
                        isFeet={isFeet}
                        loadTriggers={loadTriggers}
                        showUsages
                        totalLoadCount={totalLoadCount}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: (theme) => `calc(320px + ${theme.spacing(2)})`,
              right: 0,
            }}
          >
            <GeofenceDebugMap
              geofences={debugMapGeofences}
              waypoints={waypointMarkers}
              pings={pings}
              truckMarker={truckMarker}
              hoveredLocation={
                hoveredTriggerLocation
                  ? {
                      lat: parseFloat(hoveredTriggerLocation.lat),
                      lng: parseFloat(hoveredTriggerLocation.lon),
                    }
                  : null
              }
              isEditable={isEditable}
              onPolygonEdit={isEditable ? handleGeofencePolygonEdit : undefined}
              onCircleEdit={isEditable ? handleGeofenceCircleEdit : undefined}
              onGeofenceClick={handleGeofenceMapClick}
              sx={{ height: '100%' }}
            />
          </Box>
        </Box>

        <Box sx={{ mt: -3.25, mb: -1 }}>
          <GeofenceTimeline pings={pings} onTimeChange={setIndicatorTime} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 2,
            mt: -0.5,
            mb: -0.5,
          }}
        >
          <Tabs
            selected={bottomTab}
            onChange={setBottomTab}
            tabs={[
              {
                value: 'geofence_triggers' as BottomTab,
                label: t('admin.job_geo_debug.tab_geofence_triggers'),
              },
              {
                value: 'job_events' as BottomTab,
                label: t('admin.job_geo_debug.tab_job_events'),
              },
              {
                value: 'location_histories' as BottomTab,
                label: t('admin.job_geo_debug.tab_location_histories'),
              },
            ]}
            tabSx={{ whiteSpace: 'nowrap', flex: 'none', px: 2 }}
            sx={{ width: 'fit-content' }}
          />
          {bottomTab === 'location_histories' &&
            pings.length > LOCATION_HISTORIES_PAGE_SIZE && (
              <Pagination
                count={locationHistoriesTotalPages}
                page={Math.min(locationHistoriesPage, locationHistoriesTotalPages)}
                onChange={(_, value) => setLocationHistoriesPage(value)}
                size="small"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '0.75rem',
                    minWidth: 28,
                    height: 28,
                    margin: 0,
                    color: 'text.secondary',
                  },
                }}
              />
            )}
        </Box>

        <Box
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {bottomTab === 'geofence_triggers' && showSideBySideDiff && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                minWidth: 0,
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  '& .MuiTableCell-root': {
                    fontFamily: 'inherit',
                    paddingTop: 1,
                    paddingBottom: 1,
                    lineHeight: 1.2,
                    fontSize: '0.8125rem',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableRow-root': {
                    '& td, & th': { borderBottomWidth: 1 },
                  },
                  '& td:nth-of-type(2), & th:nth-of-type(2)': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 0,
                    width: '100%',
                  },
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={[diffGroupHeaderCellSx, { position: 'relative' }]}
                      >
                        {t('admin.job_geo_debug.diff_current')}
                        <IconButton
                          size="small"
                          onClick={() => setDiffViewExpanded((prev) => !prev)}
                          title={t('admin.job_geo_debug.diff_view_toggle')}
                          aria-label={t('admin.job_geo_debug.diff_view_toggle')}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 4,
                            transform: 'translateY(-50%)',
                            p: 0.25,
                            '& .MuiSvgIcon-root': { fontSize: 16 },
                          }}
                        >
                          {diffViewExpanded ? (
                            <UnfoldLess fontSize="inherit" />
                          ) : (
                            <UnfoldMore fontSize="inherit" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_time')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_geofence_name')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_geofence_tag')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_trigger_type')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diffViewExpanded ? (
                      sideBySideDiffRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 0.5, color: 'text.secondary' }}
                          >
                            {t('admin.job_geo_debug.no_triggers')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        sideBySideDiffRows.map((diffRow, index) => {
                          const trigger = diffRow.current;
                          const isAdded = diffRow.historical === null;
                          return (
                            <TableRow
                              key={`${diffRow.sortTime}-${trigger?.triggeredLocation.id ?? 'empty'}-${index}-right`}
                              onMouseEnter={() =>
                                trigger &&
                                setHoveredTriggerLocation({
                                  lat: trigger.triggeredLocation.lat,
                                  lon: trigger.triggeredLocation.lon,
                                })
                              }
                              onMouseLeave={() => setHoveredTriggerLocation(null)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    isAdded
                                      ? 'rgba(76, 175, 80, 0.24)'
                                      : theme.palette.action.hover,
                                },
                                backgroundColor: (theme) =>
                                  isAdded
                                    ? 'rgba(76, 175, 80, 0.12)'
                                    : index % 2 === 1
                                      ? theme.semantic.surfaceSunken
                                      : 'transparent',
                              }}
                            >
                              {trigger ? (
                                <>
                                  <TableCell>
                                    {dayjs
                                      .tz(trigger.triggeredLocation.occurredAt)
                                      .format('MMM DD, YYYY HH:mm:ss Z')}
                                  </TableCell>
                                  <TableCell>
                                    {trigger.geofence?.geofence?.name ?? '—'}
                                  </TableCell>
                                  <TableCell>
                                    {formatGeofenceTag(trigger.geofence?.tag ?? null)}
                                  </TableCell>
                                  <TableCell>
                                    {formatTriggerType(trigger.triggerType)}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })
                      )
                    ) : triggers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 0.5, color: 'text.secondary' }}
                        >
                          {t('admin.job_geo_debug.no_triggers')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      triggers.map((trigger, index) => (
                        <TableRow
                          key={`${trigger.triggeredLocation.id}-${trigger.geofence?.id ?? index}-${trigger.triggerType}`}
                          onMouseEnter={() =>
                            setHoveredTriggerLocation({
                              lat: trigger.triggeredLocation.lat,
                              lon: trigger.triggeredLocation.lon,
                            })
                          }
                          onMouseLeave={() => setHoveredTriggerLocation(null)}
                          sx={{
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.action.hover,
                            },
                            backgroundColor: (theme) =>
                              index % 2 === 1
                                ? theme.semantic.surfaceSunken
                                : 'transparent',
                          }}
                        >
                          <TableCell>
                            {dayjs
                              .tz(trigger.triggeredLocation.occurredAt)
                              .format('MMM DD, YYYY HH:mm:ss Z')}
                          </TableCell>
                          <TableCell>{trigger.geofence?.geofence?.name ?? '—'}</TableCell>
                          <TableCell>
                            {formatGeofenceTag(trigger.geofence?.tag ?? null)}
                          </TableCell>
                          <TableCell>{formatTriggerType(trigger.triggerType)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer
                component={Paper}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  '& .MuiTableCell-root': {
                    fontFamily: 'inherit',
                    paddingTop: 1,
                    paddingBottom: 1,
                    lineHeight: 1.2,
                    fontSize: '0.8125rem',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTableRow-root': {
                    '& td, & th': { borderBottomWidth: 1 },
                  },
                  '& td:nth-of-type(2), & th:nth-of-type(2)': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 0,
                    width: '100%',
                  },
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={4} sx={diffGroupHeaderCellSx}>
                        {t('admin.job_geo_debug.diff_historical')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_time')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_geofence_name')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_geofence_tag')}
                      </TableCell>
                      <TableCell sx={headerCellSx}>
                        {t('admin.job_geo_debug.column_trigger_type')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diffViewExpanded ? (
                      sideBySideDiffRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ py: 0.5, color: 'text.secondary' }}
                          >
                            {t('admin.job_geo_debug.no_triggers')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        sideBySideDiffRows.map((diffRow, index) => {
                          const trigger = diffRow.historical;
                          const isRemoved = diffRow.current === null;
                          return (
                            <TableRow
                              key={`${diffRow.sortTime}-${trigger?.triggeredLocation.id ?? 'empty'}-${index}-left`}
                              onMouseEnter={() =>
                                trigger &&
                                setHoveredTriggerLocation({
                                  lat: trigger.triggeredLocation.lat,
                                  lon: trigger.triggeredLocation.lon,
                                })
                              }
                              onMouseLeave={() => setHoveredTriggerLocation(null)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    isRemoved
                                      ? 'rgba(244, 67, 54, 0.18)'
                                      : theme.palette.action.hover,
                                },
                                backgroundColor: (theme) =>
                                  isRemoved
                                    ? 'rgba(244, 67, 54, 0.08)'
                                    : index % 2 === 1
                                      ? theme.semantic.surfaceSunken
                                      : 'transparent',
                              }}
                            >
                              {trigger ? (
                                <>
                                  <TableCell>
                                    {dayjs
                                      .tz(trigger.triggeredLocation.occurredAt)
                                      .format('MMM DD, YYYY HH:mm:ss Z')}
                                  </TableCell>
                                  <TableCell>
                                    {trigger.geofence?.geofence?.name ?? '—'}
                                  </TableCell>
                                  <TableCell>
                                    {formatGeofenceTag(trigger.geofence?.tag ?? null)}
                                  </TableCell>
                                  <TableCell>
                                    {formatTriggerType(trigger.triggerType)}
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'text.secondary',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    —
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })
                      )
                    ) : (historicalTriggers ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 0.5, color: 'text.secondary' }}
                        >
                          {t('admin.job_geo_debug.no_triggers')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      (historicalTriggers ?? []).map((trigger, index) => (
                        <TableRow
                          key={`${trigger.triggeredLocation.id}-${trigger.geofence?.id ?? index}-${trigger.triggerType}`}
                          onMouseEnter={() =>
                            setHoveredTriggerLocation({
                              lat: trigger.triggeredLocation.lat,
                              lon: trigger.triggeredLocation.lon,
                            })
                          }
                          onMouseLeave={() => setHoveredTriggerLocation(null)}
                          sx={{
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.action.hover,
                            },
                            backgroundColor: (theme) =>
                              index % 2 === 1
                                ? theme.semantic.surfaceSunken
                                : 'transparent',
                          }}
                        >
                          <TableCell>
                            {dayjs
                              .tz(trigger.triggeredLocation.occurredAt)
                              .format('MMM DD, YYYY HH:mm:ss Z')}
                          </TableCell>
                          <TableCell>{trigger.geofence?.geofence?.name ?? '—'}</TableCell>
                          <TableCell>
                            {formatGeofenceTag(trigger.geofence?.tag ?? null)}
                          </TableCell>
                          <TableCell>{formatTriggerType(trigger.triggerType)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {bottomTab === 'geofence_triggers' && !showSideBySideDiff && (
            <TableContainer
              component={Paper}
              sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                fontFamily: 'monospace',
                '& .MuiTableCell-root': {
                  fontFamily: 'inherit',
                  paddingTop: 1,
                  paddingBottom: 1,
                  lineHeight: 1.2,
                  fontSize: '0.8125rem',
                  whiteSpace: 'nowrap',
                },
                '& .MuiTableRow-root': {
                  '& td, & th': {
                    borderBottomWidth: 1,
                  },
                },
                '& td:nth-of-type(2), & th:nth-of-type(2)': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 0,
                  width: '100%',
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>
                      {t('admin.job_geo_debug.column_time')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('admin.job_geo_debug.column_geofence_name')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('admin.job_geo_debug.column_geofence_tag')}
                    </TableCell>
                    <TableCell sx={headerCellSx}>
                      {t('admin.job_geo_debug.column_trigger_type')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {triggersError && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        color="error"
                        sx={{ py: 0.5 }}
                      >
                        {t('error_messages.geofence_triggers.failed_to_fetch')}
                      </TableCell>
                    </TableRow>
                  )}
                  {!triggersLoading &&
                    !triggersError &&
                    triggersForSingleView.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 0.5, color: 'text.secondary' }}
                        >
                          {t('admin.job_geo_debug.no_triggers')}
                        </TableCell>
                      </TableRow>
                    )}
                  {!triggersLoading &&
                    !triggersError &&
                    triggersForSingleView.map((trigger, index) => (
                      <TableRow
                        key={`${trigger.triggeredLocation.id}-${trigger.geofence?.id ?? index}-${trigger.triggerType}`}
                        onMouseEnter={() =>
                          setHoveredTriggerLocation({
                            lat: trigger.triggeredLocation.lat,
                            lon: trigger.triggeredLocation.lon,
                          })
                        }
                        onMouseLeave={() => setHoveredTriggerLocation(null)}
                        sx={{
                          '&:hover': {
                            backgroundColor: (theme) => theme.palette.action.hover,
                          },
                          backgroundColor: (theme) =>
                            index % 2 === 1
                              ? theme.semantic.surfaceSunken
                              : 'transparent',
                        }}
                      >
                        <TableCell>
                          {dayjs
                            .tz(trigger.triggeredLocation.occurredAt)
                            .format('MMM DD, YYYY HH:mm:ss Z')}
                        </TableCell>
                        <TableCell>{trigger.geofence?.geofence?.name ?? '—'}</TableCell>
                        <TableCell>
                          {formatGeofenceTag(trigger.geofence?.tag ?? null)}
                        </TableCell>
                        <TableCell>{formatTriggerType(trigger.triggerType)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {bottomTab === 'job_events' && companyId && (
            <JobEventsTable
              companyId={companyId}
              jobId={jobId}
              isDirty={isDirty}
              rawTriggers={rawTriggerResponse}
              isActive={bottomTab === 'job_events'}
            />
          )}
          {bottomTab === 'location_histories' && (
            <LocationHistoriesTable
              pings={pagedPings}
              isFeet={isFeet}
              onHoverPing={setHoveredTriggerLocation}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Job ID is from the URL (stable for this page). Company ID is always the user's company.
 */
export const Geofence = observer(function Geofence() {
  const { jobId } = useParams<{ jobId: string }>();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { job, siteUsages, pings, isMapDataLoading } = useOutletContext<{
    job: Job | null;
    siteUsages: JobSiteUsage[];
    pings: LocationHistoryMinimal[];
    isMapDataLoading: boolean;
  }>();
  const isLoadingJob = !job || isMapDataLoading;

  if (!jobId) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('admin.job_geo_debug.missing_job_id')}
      </Typography>
    );
  }

  if (isLoadingJob) {
    return <CommonLoader />;
  }

  const isFeet = userStore?.userCompany?.isFeet ?? false;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <GeoDebugPane
          job={job}
          jobId={jobId}
          companyId={companyId}
          isFeet={isFeet}
          siteUsages={siteUsages}
          pings={pings}
        />
      </Box>
    </Box>
  );
});
